import * as React from 'react';
import { commonStyleSheet } from 'library/common-styles';
import withStyles from 'react-jss';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { accountStyles } from './settings.styles';
import { useTranslation } from 'react-i18next';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import {
    Button,
    Checkbox,
    Col, Form,
    Input,
    Row, Select,
    Spin,
    Tooltip,
    message,
    Drawer,
    Dropdown,
    Menu,
} from 'antd';
import {
    CloseOutlined, DeleteOutlined, SearchOutlined, PlusOutlined,
} from '@ant-design/icons';
import {
    getAddresses, getPickupRules, createPickupRule, deletePickupRule,
} from 'network/pickup.api';
import Loader from 'components/common/Loader';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { NAVBAR_HEIGHT } from 'library/globals';
import { ReduxStore } from '../../reducers/redux.types';
import { Master } from '../../types/master-data-types';
import BulkPickupSlots from './bulk-pickupslots';

const { Option } = Select;
const styles = (theme: ThemeType) => ({
    ...commonStyleSheet(theme),
    main: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '5px 6px 6px 3px',
        height: '100%',
        backgroundColor: 'transparent',
    },
    createPickupDrawer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        marginBottom: 4,
        '& .ant-drawer-header': {
            padding: 12,
        },
        '& .ant-drawer-body': {
            backgroundColor: '#FFF',
            padding: 12,
        },
    },
    daySelect: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '12px',
        width: '30%',
    },
    timeSlotSelect: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '12px',
        width: '30%',
    },
    selectHeader: {
        fontWeight: 600,
        fontSize: 16,
        color: '#333',
        padding: 0,
        justifyContent: 'space-between',
        fontFamily: 'Open Sans',
        letterSpacing: 0,
    },
    selectAddressPanel: {
        fontWeight: 500,
        fontSize: 14,
        color: '#333',
        fontFamily: 'Open Sans',
        marginBottom: '12px',
        '& .ant-collapse-borderless > .ant-collapse-item': {
            border: 'none',
        },
    },
    addPickupBtn: {
        fontFamily: 'Open Sans',
        fontSize: '12px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 600,
        fontSize: 16,
        color: '#333',
        padding: 0,
        justifyContent: 'space-between',
        fontFamily: 'Open Sans',
        letterSpacing: 0,
        '& .ant-drawer-header': {
            padding: '12px',
        },
    },
    closeIcon: {
        fontSize: 16,
        marginRight: 8,
        cursor: 'pointer',
        color: '#CCC',
    },
    selectFilter: {
        '& .ant-select:not(.ant-select-customize-input) .ant-select-selector': {
            backgroundColor: '#fafafa',
        },
        '& .ant-select-selector': {
            width: 'auto !important',
            borderRadius: '6px !important',
            alignItems: 'center',
        },
        '& .ant-select-dropdown': {
            padding: '12px !important',
            borderRadius: '6px !important',
            gap: '16px !important',
        },
        '& .ant-select-item': {
            padding: '8px !important',
            borderRadius: '4px !important',
            gap: '10px !important',
            fontWeight: 600,
        },
        '& .ant-select-item-option-selected': {
            backgroundColor: `${theme.tertiaryFillColor} !important`,
            padding: '8px !important',
            borderRadius: '4px !important',
            gap: '10px !important',
        },
    },
    heading: {
        fontWeight: 'bold',
        textAlign: 'left',
        fontSize: 'larger',
        marginTop: '20px',
        marginBottom: '20px',
    },
    addressSelection: {
        padding: '20px',
        border: '1px solid #ccc',
        width: '300px',
        margin: '0 auto',
    },
    addressItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px',
    },
    tableLoader: {
        textAlign: 'center',
        padding: '20px',
    },
    errormsg: {
        color: 'red',
    },
    addressDrawer: {
        border: '1px solid #f0f0f0',
        borderRadius: '4px',
        padding: '16px',
        marginTop: '16px',
        marginBottom: '16px',
        backgroundColor: '#fafafa',
    },
    filters: {
        display: 'flex',
        padding: '8px 16px',
        background: '#ffffff',
        fontFamily: 'Open Sans',
        fontSize: 12,
        gap: 12,
        justifyContent: 'end',
        '& .ant-btn': {
            boxSizing: 'border-box',
            height: 30,
            borderRadius: 4,
            fontFamily: 'Open Sans',
            fontSize: 12,
            fontWeight: 600,
            laterSpacing: 0,
        },
        '& .ant-btn-ghost': {
            border: `1px solid ${theme.primaryColor} `,
        },
        '& .ant-btn-primary, .ant-btn-primary:hover, .ant-btn-primary:focus': {
            backgroundColor: theme.primaryColor,
        },
        '& .ant-dropdown-menu-submenu-title': {
            padding: '12px 4px',
        },
    },
});

/* eslint-disable max-len */
interface SchedulePickupProps extends StylesProps<ReturnType<typeof styles>> {
    master: Master;
}

interface Address {
    clientId: string;
    pincode: string;
    cityName: string;
    stateName: string;
    name: string;
    phone: string;
    email?: string;
    addressLine1: string;
    addressLine2?: string;
    isInternational: boolean;
    countryName?: string;
    id: string;
    alternatePhone?: string;
    alternatePhone2?: string;
    addressCode?: string;
    notes?: string;
}

const SchedulePickup = (props: SchedulePickupProps) => {
    const { classes, master } = props;
    const { t } = useTranslation();
    const [newAddressForm] = Form.useForm();
    const [pickupData, setPickupData] = React.useState([]);
    const [addressList, setAddressList] = React.useState<Address[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [tableLoading, setTableLoading] = React.useState(false);
    const [selectedAddress, setSelectedAddress] = React.useState<Address | null>(null);
    const [showSelectedAddress, setShowSelectedAddress] = React.useState(false);
    const [searchLoading, setSearchLoading] = React.useState(false);
    const [day, setDay] = React.useState(null);
    const [timeSlot, setTimeSlot] = React.useState(null);
    const [singlePickupDrawerVisible, setSinglePickupDrawerVisible] = React.useState(false);
    const [addNewAddressFormVisible, setAddNewAddressFormVisible] = React.useState(false);
    const [isModalVisibleForBulkPickupSlot, setIsModalVisibleForBulkPickupSlot] = React.useState(false);

    const showModalForBulkPickupSlot = () => {
        setIsModalVisibleForBulkPickupSlot(!isModalVisibleForBulkPickupSlot);
    };

    const bannerHeight = (master?.config?.customer_portal_config?.password_policy_banner_expiry_date
        && new Date(master?.config?.customer_portal_config?.password_policy_banner_expiry_date) <= new Date()) ? 0 : 50;

    const fetchPickupRules = async () => {
        setTableLoading(true);
        try {
            const response = await getPickupRules();
            if (response.isSuccess) {
                setPickupData(response.data.rules);
            } else {
                message.error(response.errorMessage);
            }
        } catch (error) {
            message.error('Failed to fetch pickup rules');
        } finally {
            setTableLoading(false);
        }
    };

    const fetchAddressesAndRules = async () => {
        setLoading(true);
        try {
            const response = await getAddresses({ pageNumber: 1, resultPerPage: 5 });
            setAddressList(response.data.addressBook);
        } catch (errorMsg: any) {
            message.error(errorMsg.message);
        } finally {
            setLoading(false);
        }
        fetchPickupRules();
    };

    React.useEffect(() => {
        fetchAddressesAndRules();
    }, []);

    const handleDelete = async (record: any) => {
        const rule = { id: record.id };
        setTableLoading(true);
        try {
            const response = await deletePickupRule(rule);
            if (response.isSuccess) {
                await fetchPickupRules();
                message.success('Pickup rule deleted successfully');
            } else {
                message.error(response.errorMessage);
            }
        } catch (error) {
            message.error('Failed to delete pickup rule');
        } finally {
            setTableLoading(false);
        }
    };

    const columns = [
        {
            title: 'Sl no',
            dataIndex: 'slNo',
            key: 'slNo',
            width: 80,
        },
        {
            title: 'Day',
            dataIndex: 'day',
            key: 'day',
            width: 100,
        },
        {
            title: 'Pick Time Slot',
            dataIndex: 'timeSlot',
            key: 'timeSlot',
            width: 140,
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 85,
            render: (record: any) => (
                <Tooltip title={t('delete')}>
                    <DeleteOutlined onClick={() => handleDelete(record)} />
                </Tooltip>
            ),
        },
    ];

    const handleFormSubmit = async () => {
        if (!selectedAddress) {
            message.error('Please select an address');
            return;
        }

        const [pickupStart, pickupEnd] = timeSlot.split('-');
        const {
            addressLine1,
            addressLine2,
            cityName,
            stateName,
            countryName,
            pincode,
            phone,
            notes,
        } = selectedAddress;

        const address = {
            addressLine1,
            addressLine2,
            cityName,
            stateName,
            countryName,
            pincode,
            phone,
            notes,
        };

        const finalObject = {
            day,
            pickupStart,
            pickupEnd,
            address,
        };

        setTableLoading(true);
        newAddressForm.resetFields();
        try {
            const response = await createPickupRule(finalObject);
            if (response.isSuccess) {
                await fetchPickupRules();
                message.success('Pickup rule created successfully');
                setDay(null);
                setTimeSlot(null);
                setSelectedAddress(null);
                setShowSelectedAddress(false);
            } else {
                message.error(response.errorMessage);
            }
        } catch (error) {
            message.error('Failed to create pickup rule');
        } finally {
            setTableLoading(false);
        }
    };

    const handleAddressSelection = (address: Address) => {
        setSelectedAddress(address);
    };

    const handleRemoveSelectedAddress = () => {
        setSelectedAddress(null);
        setShowSelectedAddress(false);
    };

    const handleNewAddressSubmit = (values: any) => {
        // Create a new address object from form values
        const newAddress = {
            id: `new-${Date.now()}`, // Generate a temporary ID
            clientId: '',
            isInternational: false,
            name: values.companyName || '',
            ...values,
        };

        setSelectedAddress(newAddress);
        setShowSelectedAddress(true);
        setAddNewAddressFormVisible(false);
    };

    const fetchAddresses = async (searchString: string) => {
        setSearchLoading(true);
        try {
            const response = await getAddresses({ pageNumber: 1, resultPerPage: 5, searchString });
            if (response.isSuccess) {
                setAddressList(response.data.addressBook);
            } else {
                message.error(response.errorMessage);
            }
        } catch (error) {
            message.error('Failed to fetch addresses');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchString = e.target.value;
        if (searchString) {
            fetchAddresses(searchString);
        } else {
            // If search field is cleared, fetch all addresses
            fetchAddressesAndRules();
        }
    };

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 7; hour <= 21; hour += 1) {
            const start = `${hour.toString().padStart(2, '0')}:00`;
            const end = `${(hour + 2).toString().padStart(2, '0')}:00`;
            slots.push(`${start}-${end}`);
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();

    const daysOfWeek = [
        { label: 'Sunday', value: 'Sunday' },
        { label: 'Monday', value: 'Monday' },
        { label: 'Tuesday', value: 'Tuesday' },
        { label: 'Wednesday', value: 'Wednesday' },
        { label: 'Thursday', value: 'Thursday' },
        { label: 'Friday', value: 'Friday' },
        { label: 'Saturday', value: 'Saturday' },
    ];

    const renderDaySelect = () => {
        return (
            <div className={classes.daySelect}>
                <div className={classes.selectHeader}>{t('select_a_day')}</div>
                <Select
                    className={classes.selectFilter}
                    value={day}
                    allowClear
                    onChange={(value) => setDay(value)}
                    options={daysOfWeek}
                    placeholder={t('day')}
                    dropdownMatchSelectWidth={false}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
            </div>
        );
    };

    const renderTimeSlotSelect = () => {
        return (
            <div className={classes.timeSlotSelect}>
                <div className={classes.selectHeader}>{t('select_a_timeSlot')}</div>
                <Select
                    className={classes.selectFilter}
                    onChange={(value) => setTimeSlot(value)}
                    allowClear
                    placeholder={t('time_slot')}
                    value={timeSlot}
                    dropdownMatchSelectWidth={false}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                    {timeSlots.map((slot) => (
                        <Option key={slot} value={slot}>{slot}</Option>
                    ))}
                </Select>
            </div>
        );
    };

    const renderAddNewAddressForm = () => {
        if (!addNewAddressFormVisible) {
            return null;
        }

        return (
            <div className={classes.addressDrawer}>
                <Form
                    form={newAddressForm}
                    layout="vertical"
                    onFinish={handleNewAddressSubmit}
                >
                    <Form.Item
                        name="companyName"
                        label="Company name"
                        style={{ marginBottom: '8px' }}
                    >
                        <Input placeholder="Company Name" />
                    </Form.Item>
                    <Form.Item
                        name="addressLine1"
                        label="Address Line 1"
                        rules={[{ required: true, message: 'Address Line 1 is required' }]}
                        style={{ marginBottom: '8px' }}
                    >
                        <Input placeholder="Address Line 1" />
                    </Form.Item>
                    <Form.Item
                        name="addressLine2"
                        label="Address Line 2"
                        style={{ marginBottom: '8px' }}
                    >
                        <Input placeholder="Address Line 2" />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="pincode"
                                label="PIN Code"
                                rules={[{ required: true, message: 'PIN Code is required' }]}
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="PIN Code" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="cityName"
                                label="City"
                                rules={[{ required: true, message: 'City is required' }]}
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="City" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="stateName"
                                label="State"
                                rules={[{ required: true, message: 'State is required' }]}
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="State" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="countryName"
                                label="Country"
                                rules={[{ required: true, message: 'Country is required' }]}
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="Country" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="latitude"
                                label="Latitude"
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="Latitude" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="longitude"
                                label="Longitude"
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="Longitude" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label="Mobile Number"
                                rules={[{ required: true, message: 'Mobile Number is required' }]}
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="Mobile Number" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="alternateMobileNumber"
                                label="Alternate Mobile Number"
                                style={{ marginBottom: '8px' }}
                            >
                                <Input placeholder="Alternate Mobile Number" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('save_and_use_as_pickup_address')}
                        </Button>
                        <Button
                            style={{ marginLeft: '8px' }}
                            onClick={() => setAddNewAddressFormVisible(false)}
                        >
                            {t('cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        );
    };

    const renderSelectSavedAddress = () => {
        if (addNewAddressFormVisible) {
            return null;
        }
        return (
            <div className={classes.addressDrawer}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Input
                        placeholder="Search"
                        prefix={<SearchOutlined />}
                        onChange={handleSearchChange}
                        style={{ marginBottom: '16px', width: '50%' }}
                    />
                    <Button
                        type="primary"
                        className={classes.addPickupBtn}
                        icon={<PlusOutlined />}
                        onClick={() => setAddNewAddressFormVisible(true)}
                    >
                        {t('add_new_address')}
                    </Button>
                </div>
                <div style={{
                    marginBottom: '2rem',
                    padding: '0.5rem',
                    alignItems: 'center',
                }}
                >
                    {searchLoading ? (
                        <div className={classes.tableLoader}>
                            <Spin />
                        </div>
                    ) : (
                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                            {addressList.length > 0 ? (
                                addressList.map((address) => (
                                    <div className={classes.addressItem} key={address.id}>
                                        <Checkbox
                                            checked={selectedAddress?.id === address.id}
                                            onChange={() => handleAddressSelection(address)}
                                        >
                                            <div style={{
                                                marginLeft: '0.5rem',
                                                marginBottom: '0.5rem',
                                                padding: '5px',
                                            }}
                                            >
                                                <div>
                                                    {`${address.name || ''}, ${address.addressLine1}${address.addressLine2 ? `, ${address.addressLine2}` : ''}, ${address.cityName}, ${address.stateName}, ${address.pincode}`}
                                                </div>
                                            </div>
                                        </Checkbox>
                                    </div>
                                ))
                            ) : (
                                <div>No addresses found</div>
                            )}
                        </div>
                    )}
                </div>
                <Button
                    type="primary"
                    onClick={() => {
                        setShowSelectedAddress(true);
                    }}
                    disabled={!selectedAddress}
                    style={{ marginRight: '8px' }}
                >
                    {t('select_address')}
                </Button>
            </div>
        );
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                bordered={false}
                pagination={false}
                loading={tableLoading}
                dataSource={pickupData}
                columns={columns}
                rowKey="slNo"
                scroll={{ y: 400 }}
                style={{
                    height: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 55px) - ${bannerHeight}px) )`,
                    background: '#ffffff',
                }}
            />
        );
    };

    if (loading) {
        return <Loader zIndex={10} />;
    }

    const renderHeader = () => {
        return (
            <div className={classes.header}>
                <div>
                    <CloseOutlined onClick={() => setSinglePickupDrawerVisible(false)} alt="close" className={classes.closeIcon} />
                    <span>
                        {t('add_pickup_schedule')}
                    </span>
                </div>
            </div>
        );
    };

    const renderSelectAddress = () => {
        return (
            <div className={classes.selectAddressPanel}>
                <div className={classes.selectHeader}>{t('select_an_address')}</div>
                {renderSelectSavedAddress()}
                {renderAddNewAddressForm()}
            </div>
        );
    };

    const renderSinglePickupDrawer = () => {
        if (!singlePickupDrawerVisible) {
            return null;
        }
        return (
            <Drawer
                className={classes.createPickupDrawer}
                visible={singlePickupDrawerVisible}
                title={renderHeader()}
                onClose={() => setSinglePickupDrawerVisible(false)}
                closable={false}
                width="40%"
            >
                <div>
                    {renderDaySelect()}
                    {renderTimeSlotSelect()}
                    {renderSelectAddress()}
                    {showSelectedAddress && (
                        <div>
                            <div className={classes.selectHeader}>Selected Address :</div>
                            <div>
                                {selectedAddress
                                    ? `${selectedAddress.addressLine1}${selectedAddress.addressLine2
                                        ? `, ${selectedAddress.addressLine2}`
                                        : ''}, ${selectedAddress.cityName}, ${selectedAddress.stateName}, ${selectedAddress.pincode}`
                                    : 'No address selected'}
                            </div>
                            <Button type="link" onClick={handleRemoveSelectedAddress}>
                                <CloseOutlined alt="close" className={classes.closeIcon} />
                            </Button>
                        </div>
                    )}
                    <Button
                        type="primary"
                        htmlType="submit"
                        disabled={!selectedAddress || !day || !timeSlot}
                        onClick={() => handleFormSubmit()}
                    >
                        {t('add_pickup_slot')}
                    </Button>
                </div>
            </Drawer>
        );
    };

    const renderAddPickupScheduleButton = () => {
        return (
            <Button
                type="primary"
                className={classes.addPickupBtn}
                icon={<PlusOutlined />}
            >
                {t('add_pickup_schedule')}
            </Button>
        );
    };

    const handleCancel = () => {
        setIsModalVisibleForBulkPickupSlot(false);
    };

    const renderBulkPickupDrawer = () => {
        return (
            <BulkPickupSlots
                isModalVisible={isModalVisibleForBulkPickupSlot}
                handleCancel={handleCancel}
                tableLoading={tableLoading}
                setTableLoading={setTableLoading}
                fetchPickupRules={fetchPickupRules}
            />
        );
    };

    const addPickupMenu = (
        <Menu>
            <Menu.Item
                key="single"
                onClick={() => setSinglePickupDrawerVisible(true)}
            >
                {t('single_address')}
            </Menu.Item>
            <Menu.Item
                key="bulk"
                onClick={showModalForBulkPickupSlot}
            >
                {t('upload_excel_address')}
            </Menu.Item>
        </Menu>
    );

    const renderAddPickupScheduleDropdown = () => {
        return (
            <Dropdown
                overlay={addPickupMenu}
                trigger={['click']}
            >
                {renderAddPickupScheduleButton()}
            </Dropdown>
        );
    };


    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderAddPickupScheduleDropdown()}
            </div>
        );
    };

    return (
        <div className={classes.main}>
            {renderFilters()}
            {renderTable()}
            {renderBulkPickupDrawer()}
            {renderSinglePickupDrawer()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    return {
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default withStyles(accountStyles)(GenericHoc(hocConfig)(SchedulePickup));
